import { Component, Input, OnInit, ViewChild, ViewEncapsulation } from "@angular/core";
import { RequestsService } from "../../../services/requests.service";
import { NewRequestService } from "../../../models/request/new-request/new-request-service";
import { GridComponent } from "@progress/kendo-angular-grid";
import { RequestServicesService } from "../../../services/request-services.service";
import { CategoryServicesService } from "../../../services/category-services.service";
import { KeyValueObject } from "../../../models/core/KeyValueObject";
import { DropDownFilterSettings } from "@progress/kendo-angular-dropdowns";
import { ServiceItem } from "../../../models/services/service-item";
import { SecurityService } from "../../../../core/services/security.service";
import { NotificationService } from "../../../../core/services/notification.service";
import { NotificationType } from "../../../../core/services/notification-type";
import { SetRequestServicesDiscount, SetRequestServicesDiscountItem } from "../../../models/discount/set-request-services-discount";
import { DiscountLevelsService } from "../../../services/discount-levels.service";
import { LookupService } from "../../../services/lookup.service";
import { forkJoin } from "rxjs";
import { DialogService } from "@progress/kendo-angular-dialog";
import { ServicesHierarchicalSelectorComponent } from "../../services-hierarchical-selector/services-hierarchical-selector.component";
import { MultipleRequestServiceItem, MultipleRequestServices } from "../../../models/request/multiple-request-services";
import { ContragentsService } from "../../../../categories/contragents/contragents.service";
import { PaymentStatusEnum } from "../../../enums/payment-status.enum";

@Component({
    selector: 'request-page-agreement-services',
    templateUrl: './request-page-agreement-services.component.html',
    styleUrls: ['./request-page-agreement-services.component.scss',
        '../../../../../vendor/libs/angular2-ladda/angular2-ladda.scss',
        '../../../../../vendor/libs/ngx-perfect-scrollbar/ngx-perfect-scrollbar.scss',
    ],
    encapsulation: ViewEncapsulation.None,
})
export class RequestPageAgreementServicesComponent implements OnInit {

    @ViewChild("additionalRequestServicesGrid") additionalRequestServicesGrid: GridComponent;

    @Input() requestId: number;
    @Input() requestTypeId: number;
    @Input() requestCategoryId: number;
    @Input() customerContragentId: number;
    @Input() paymentStatusId: number;
    @Input() canEditRequest: boolean = true;

    fullService: ServiceItem[] = [];
    services: KeyValueObject[] = [];
    costCenters: KeyValueObject[] = [];
    requestServices: NewRequestService[] = [];

    roleForceDiscount: boolean;
    roleDiscountLimit: number;
    maxDiscountLimit: number;

    basicServicesExpanded: boolean = false;

    loadingBasic: boolean = false;
    loadingAdditional: boolean = false;

    contragentPaymentTypeId: number;

    dropDownFilterSettings: DropDownFilterSettings = { caseSensitive: false, operator: 'contains' };

    constructor(
        private requestsService: RequestsService,
        private requestServicesService: RequestServicesService,
        private categoryServicesService: CategoryServicesService,
        private securityService: SecurityService,
        private notificationService: NotificationService,
        private discountLevelsService: DiscountLevelsService,
        private lookupService: LookupService,
        private dialogService: DialogService,
        private contragentsService: ContragentsService,
    ) { }

    ngOnInit(): void {
        this.roleForceDiscount = this.securityService.currentUser.forceDiscount;
        this.roleDiscountLimit = this.securityService.currentUser.discountLimit;

        this.loadRequestServices(null);

        const services$ = this.categoryServicesService.list();
        const costCenters$ = this.lookupService.getData("cost-centers", null);
        const contragent$ = this.contragentsService.getContragentWithContact(this.customerContragentId);

        forkJoin(services$, costCenters$, contragent$)
            .subscribe(([services, costCenters, contragent]) => {
                this.fullService = services.data;
                this.services = services.data.map(x => new KeyValueObject(x.id, x.name));
                this.costCenters = costCenters;

                if (contragent) {
                    this.contragentPaymentTypeId = contragent.paymentTypeId;
                }
            });
    }

    loadRequestServices(basic: boolean = null) {
        this.turnOnLoading(basic);
        this.requestsService.getRequestServices(this.requestId).subscribe(resp => {
            resp.forEach(x => x.discountPrimary = x.discount);
            this.requestServices = resp;
            this.requestServices = this.groupServices(this.requestServices);
            this.turnOffLoading(basic);
        });
    }

    groupServices = (devices: NewRequestService[]) => {
        return Object.values(
            devices.reduce((acc, item) => {
                const key = `${item.serviceId}_${item.parentRequestServiceId}`;
                if (!acc[key]) acc[key] = { ...item, requestServiceIds: [], count: 0 };
                acc[key].requestServiceIds.push(item.requestServiceId);
                acc[key].count += item.count;
                return acc;
            }, {} as Record<string, NewRequestService>));
    }

    serviceChange(dataItem: NewRequestService) {
        this.calculateMaxDiscountLimit(dataItem);
    }

    createNewAdditionalRequestService(): NewRequestService {
        var requestService = new NewRequestService();
        requestService.requestId = this.requestId;
        requestService.isDefault = false;
        return requestService;
    }

    saveAdditionalRequestServiceHandler({ dataItem }: any) {
        this.saveRequestServiceHandler(dataItem, false);
    }

    cellClickAdditionalRequestServiceHandler({ dataItem, sender, rowIndex, columnIndex }) {
        if (columnIndex < 9) {
            this.editAdditionalRequestServiceHandler({ dataItem, sender, rowIndex });
        }
    }

    editAdditionalRequestServiceHandler({ dataItem, sender, rowIndex }: any) {
        this.calculateMaxDiscountLimit(dataItem);
        sender.editRow(rowIndex);
    }

    saveRequestServiceHandler(dataItem: NewRequestService, basic: boolean) {
        this.turnOnLoading(basic);
        let requestServiceIds = dataItem.requestServiceIds.filter(x => x != 0);
        const requests$ = [];

        if (dataItem.count < requestServiceIds.length) {
            const removeCount = requestServiceIds.length - dataItem.count;
            const idsToRemove = requestServiceIds.sort((a, b) => b - a).slice(0, removeCount);
            requestServiceIds = requestServiceIds.filter(x => !idsToRemove.includes(x));
            const removes$ = idsToRemove.map(requestServiceId =>
                this.requestServicesService.remove(requestServiceId.toString(), null, null, true));
            requests$.push(...removes$);
        }

        if (dataItem.count > requestServiceIds.length) {
            const createCount = dataItem.count - requestServiceIds.length;
            const creates$ = Array.from({length: createCount}, (_, i) => i).map(_ =>
                this.requestServicesService.create({
                    ...dataItem,
                    requestId: this.requestId,
                    requestServiceId: 0,
                }, null));
            requests$.push(...creates$);
        }

        if (requestServiceIds.length > 0) {
            const updates$ = requestServiceIds.map(requestServiceId =>
                this.requestServicesService.update({
                    ...dataItem,
                    requestServiceId: requestServiceId,
                }, null));
            requests$.push(...updates$);
        }

        forkJoin(requests$).subscribe(() => {
            this.loadRequestServices(basic);
        });
    }

    removeRequestServiceHandler({ dataItem }: any) {
        this.turnOffLoading(false);
        const requestServiceIds = dataItem.requestServiceIds.filter(x => x != 0);
        const removes$ = requestServiceIds.map(requestServiceId =>
            this.requestServicesService.remove(requestServiceId.toString(), null, null, true));
        forkJoin(removes$).subscribe(() => {
            this.loadRequestServices();
            this.turnOffLoading(false);
        });
    }

    addNewAdditionalRequestService() {
        const dialogRef = this.dialogService.open({
            content: ServicesHierarchicalSelectorComponent,
            title: 'Добавление услуг',
            width: '80%',
            height: '820px',
            minWidth: '800px'
        });
        const serviceSelector = <ServicesHierarchicalSelectorComponent>dialogRef.content.instance;

        serviceSelector.height = 460;
        serviceSelector.defaultRequestTypeId = this.requestTypeId;
        serviceSelector.defaultRequestCategoryId = this.requestCategoryId;
        serviceSelector.existingServices = this.requestServices;
        serviceSelector.requestId = this.requestId;
        serviceSelector.onCancel.subscribe(() => {
            dialogRef.close();
        });
        serviceSelector.onContinue.subscribe((services: NewRequestService[]) => {
            dialogRef.close();
            this.turnOnLoading(false);

            const result: NewRequestService[] = [];
            for (const service of services) {
                for (let i = 0; i < service.count; i++) {
                    result.push({ ...service, count: 1 });
                }
            }

            let multipleCreateModel = {
                requestId: this.requestId,
                items: result.map(service => {
                    return {
                        serviceId: service.serviceId,
                        isDefault: false,
                        cost: service.cost
                    } as MultipleRequestServiceItem;
                })
            } as MultipleRequestServices;

            this.requestServicesService.createMultiple(multipleCreateModel).subscribe(() => {
                this.loadRequestServices(false);
            });
        });
    }

    turnOnLoading(basic: boolean = null) {
        if (basic == null) {
            this.loadingBasic = true;
            this.loadingAdditional = true;
        } else {
            if (basic) this.loadingBasic = true;
            else this.loadingAdditional = true;
        }
    }

    turnOffLoading(basic: boolean = null) {
        if (basic == null) {
            this.loadingBasic = false;
            this.loadingAdditional = false;
        } else {
            if (basic) this.loadingBasic = false;
            else this.loadingAdditional = false;
        }
    }

    calculateMaxDiscountLimit(requestService: NewRequestService, forDiscountLevel: boolean = false): number {
        if (this.roleForceDiscount) {
            if (forDiscountLevel) {
                this.maxDiscountLimit = this.fullService.find(x => x.id == requestService.serviceId).discountLimit;
            } else {
                this.maxDiscountLimit = 100;
            }
        } else if (requestService.serviceId != null) {
            let serviceDiscountLimit = this.fullService.find(x => x.id == requestService.serviceId).discountLimit;
            this.maxDiscountLimit = this.roleDiscountLimit > serviceDiscountLimit
                ? serviceDiscountLimit
                : this.roleDiscountLimit;
        } else {
            this.maxDiscountLimit = 0;
        }

        return this.maxDiscountLimit;
    }

    getServiceSum(requestService: NewRequestService): number {
        return requestService.count * this.getCostWithDiscount(requestService);
    }

    getCostWithDiscount(requestService: NewRequestService): number {
        if (requestService.fullCost && requestService.discount) {
            let cost = requestService.fullCost - (requestService.fullCost * requestService.discount / 100);
            return Number.isInteger(cost) ? cost : Math.floor(cost);
        }

        return requestService.fullCost;
    }

    discountChange(dataItem: NewRequestService) {
        if (this.discountInvalid(dataItem) && dataItem.discount > 0) {
            this.notificationService.error({
                title: `Вы превысили максимальный размер скидки - ${this.maxDiscountLimit}%`,
                notificationType: NotificationType.Toast,
                message: ''
            });
        }
    }

    discountInvalid(requestService: NewRequestService): boolean {
        return requestService.discount != null &&
            (requestService.discount < 0 ||
                requestService.discount > 100 ||
                requestService.discount > this.maxDiscountLimit);
    }

    changeDiscountLevel(rate: number) {
        const requestServices = this.additionalRequestServices;

        if (rate != null) {
            requestServices.forEach(x => {
                let maxDiscount = this.calculateMaxDiscountLimit(x, true);
                x.discount = maxDiscount * rate;
            });
        } else {
            requestServices.forEach(requestService => requestService.discount = requestService.discountPrimary);
        }
    }

    commitDiscountLevel(rate: number) {
        const requestServices = this.additionalRequestServices;

        let requestServiceItems: SetRequestServicesDiscountItem[] = [];

        requestServices.forEach(requestService => {
            requestService.requestServiceIds.forEach(requestServiceId => {
                let item = {
                    requestServiceId: requestServiceId,
                    discount: Math.floor(requestService.discount)
                } as SetRequestServicesDiscountItem;
                requestServiceItems.push(item);
            });
        });

        let setDiscountModel = {
            requestServices: requestServiceItems
        } as SetRequestServicesDiscount;

        this.turnOnLoading(false);
        this.discountLevelsService.setRequestServicesDiscount(setDiscountModel).subscribe(() => {
            this.loadRequestServices(false);
        });
    }

    canSaveAdditionalRequestService(dataItem: NewRequestService): boolean {
        return dataItem.serviceId && !this.discountInvalid(dataItem) && (dataItem.cost == null || dataItem.cost > 0) && dataItem.count >= 1;
    }

    toggleBasicServices() {
        this.basicServicesExpanded = !this.basicServicesExpanded;
    }

    get basicRequestServices(): NewRequestService[] {
        return this.requestServices.filter(x => x.isDefault == true);
    }

    get basicGridData(): NewRequestService[] {
        const parents = this.basicRequestServices.filter(x => x.parentRequestServiceId == null);
        if (!this.basicServicesExpanded) return parents;
        const children = this.basicRequestServices.filter(x => x.parentRequestServiceId != null);
        return [...parents, ...children];
    }

    get additionalRequestServices(): NewRequestService[] {
        return this.requestServices.filter(x => x.isDefault == false);
    }

    get hasDiscountRight(): boolean {
        return this.securityService.hasClaim('role-Discounting-right');
    }

    get isAdmin(): boolean {
        return this.securityService.isAdmin();
    }

    get addServiceAdditionalDisabled(): boolean {
        return (this.paymentStatusId != null && this.paymentStatusId != <number>PaymentStatusEnum.waitIssueInvoice)
            || this.loadingAdditional;
    }

    public rowClass() {
        return {
            'cursor-pointer': true
        };
    }

    public basicRowClass = (context: any) => {
        return {
            'cursor-pointer': true,
            'basic-service-parent-row': context.dataItem.parentRequestServiceId == null,
        };
    }

    get totalPrice(): number {
        const priceServices = this.requestServices.some(x => x.isDone)
            ? this.requestServices.filter(x => x.isDone)
            : this.requestServices;

        const totalPrice = priceServices.reduce((sum, item) => sum + (this.getCostWithDiscount(item) * item.count), 0);

        return Math.floor(totalPrice);
    }
}
